import { inject, Injectable } from '@angular/core'
import { FormBuilder, Validators } from '@angular/forms'
import { map, Observable } from 'rxjs'

import { PDFDownloadLink } from '../data/pdf-download-link'
import { EligibilityBenefits } from '../model/eligibility/eligibility-benefits.model'
import { EstimateInputs } from '../model/estimate/estimate-inputs'
import { ShopperEstimate, ShopperEstimateData, ShopperEstimateInsurance } from '../model/shopper-estimate'
import { ApiService } from './api.service'
import { CrudService } from './crud.service'

export interface EligibilityNavigationSeed {
  shopperInsurances: { P: ShopperEstimateInsurance }
  eligibilityId: string
  inputs: Pick<EstimateInputs, 'deductible' | 'coinsurance' | 'copayment' | 'outOfPocketMax' | 'insuranceType' | 'estimateType' | 'insuranceCarrier'>
  benefits: EligibilityBenefits
}

@Injectable({
  providedIn: 'root',
})
export class ShopperEstimateService {
  private readonly crudService = inject(CrudService)
  private readonly apiService = inject(ApiService)
  private readonly fb = inject(FormBuilder)

  readonly shopperForm = this.fb.group({
    patientFirstName: this.fb.control('', Validators.required),
    patientLastName: this.fb.control('', Validators.required),
    patientDOB: this.fb.control('', Validators.required),
    patientEmail: this.fb.control<string | null>(null),
    patientPhone: this.fb.control<string | null>(null),
  })

  get(id: string): Observable<ShopperEstimate> {
    return this.crudService.read(ShopperEstimate, id)
  }

  calculateNew(data: Partial<ShopperEstimateData>): Observable<ShopperEstimate> {
    return this.apiService.post<ShopperEstimateData>('/shopper-estimates/calculate', data)
      .pipe(map(resp => new ShopperEstimate(resp.data)))
  }

  calculate(id: string, data: Partial<ShopperEstimateData>): Observable<ShopperEstimate> {
    return this.apiService.post<ShopperEstimateData>(
      `/shopper-estimates/${id}/calculate`,
      data,
    ).pipe(map(resp => new ShopperEstimate(resp.data)))
  }

  sendMessage(id: string, data: { method: 'email' | 'sms'; to?: string; what?: 'link' | 'pdf' }): Observable<any> {
    return this.apiService.post<any>(
      `/shopper-estimates/${id}/message`,
      data,
    ).pipe(map(resp => resp.data))
  }

  finalize(id: string): Observable<ShopperEstimate> {
    const model = new ShopperEstimate({ id, status: ShopperEstimate.STATUS.FINALIZED })
    return this.crudService.patch(model, ['status'])
  }

  markDraft(id: string): Observable<ShopperEstimate> {
    const model = new ShopperEstimate({ id, status: ShopperEstimate.STATUS.DRAFT })
    return this.crudService.patch(model, ['status'])
  }

  downloadPDF(id: string): Observable<PDFDownloadLink> {
    return this.apiService.get<PDFDownloadLink>(`/shopper-estimates/${id}/pdf`)
      .pipe(map(resp => resp.data))
  }

  delete(id: string): Observable<void> {
    const model = new ShopperEstimate({ id } as ShopperEstimateData)
    return this.crudService.delete(model).pipe(map(() => void 0))
  }

  applyNavigationState(): EligibilityNavigationSeed | null {
    const state = history.state as Record<string, any>

    if (state?.patientFirstName) {
      this.shopperForm.patchValue({
        patientFirstName: state.patientFirstName ?? '',
        patientLastName: state.patientLastName ?? '',
        patientDOB: state.patientDOB ?? '',
      })
    }

    if (!state?.eligibilityId) {
      return null
    }

    const primaryInsurance: ShopperEstimateInsurance = {
      carrier: state.carrier,
      memberId: state.memberId,
      eligibilityId: state.eligibilityId,
      deductible: state.deductible ?? 0,
      coinsurance: state.coinsurance ?? 0,
      copayment: state.copayment ?? 0,
      outOfPocketMax: state.outOfPocketMax ?? null,
    }

    const benefits = new EligibilityBenefits()
    benefits.deductible = state.deductible ?? 0
    benefits.coinsurance = state.coinsurance ?? 0
    benefits.copayment = state.copayment ?? 0
    benefits.outOfPocketMax = state.outOfPocketMax ?? 0
    benefits.copaymentAmbiguous = state.copaymentAmbiguous ?? false
    benefits.coinsuranceAmbiguous = state.coinsuranceAmbiguous ?? false

    return {
      shopperInsurances: { P: primaryInsurance },
      eligibilityId: state.eligibilityId,
      inputs: {
        deductible: state.deductible ?? 0,
        coinsurance: state.coinsurance ?? 0,
        copayment: state.copayment ?? 0,
        outOfPocketMax: state.outOfPocketMax ?? null,
        insuranceType: 'medical',
        estimateType: 'patientpal',
        insuranceCarrier: {
          lookupId: state.lookupId,
          clinicCarrierName: state.carrier,
        },
      },
      benefits,
    }
  }

  resetForm(): void {
    this.shopperForm.reset({
      patientFirstName: '',
      patientLastName: '',
      patientDOB: '',
      patientEmail: null,
      patientPhone: null,
    })
  }
}
